import { useTheme } from '@veneer/theme';
import { SerializedStyles, css } from '@emotion/react';
import getPillBorder from '../../utils/get_pill_border';
import { makeHorizontalVisualCue } from '../../utils/visual_cue';
import { doubleFocusRing } from '../../utils/focus_ring';

type StyleKeys = {
  root: {
    base: SerializedStyles;
    attached: {
      hcOrOutlined: SerializedStyles;
      hc: SerializedStyles;
      overflow: {
        base: SerializedStyles;
        hc: SerializedStyles;
        pill: SerializedStyles;
      };
      roundNoOverflow: SerializedStyles;
      pill: SerializedStyles;
    };
    online: {
      base: SerializedStyles;
      contained: {
        base: SerializedStyles;
        hc: SerializedStyles;
        pill: SerializedStyles;
      };
      extendedHcOverflow: SerializedStyles;
    };
  };

  tabs: {
    base: SerializedStyles;
    onlineContained: SerializedStyles;
    roundNoOverflow: SerializedStyles;
    pillNoOverflow: SerializedStyles;
    noContentPanel: {
      base: SerializedStyles;
      dropShadow: SerializedStyles;
    };
    hc: SerializedStyles;
    outlined: SerializedStyles;
    extended: SerializedStyles;
    dropShadow: {
      base: SerializedStyles;
      overflow: SerializedStyles;
    };
  };

  tab: {
    base: SerializedStyles;
    hc: SerializedStyles;
    disabled: SerializedStyles;
    attached: {
      base: SerializedStyles;
      hc: SerializedStyles;
      pill: SerializedStyles;
    };
    online: SerializedStyles;
    contained: SerializedStyles;
    onlineContained: SerializedStyles;
    pill: SerializedStyles;
    outlined: SerializedStyles;
    selected: {
      base: SerializedStyles;
      hc: SerializedStyles;
      attached: {
        base: SerializedStyles;
        hc: SerializedStyles;
      };
      online: {
        base: SerializedStyles;
        hc: SerializedStyles;
        contained: {
          base: SerializedStyles;
          hc: SerializedStyles;
        };
      };
      outlined: {
        base: SerializedStyles;
        hc: SerializedStyles;
      };
      dropShadow: SerializedStyles;
    };
    notification: SerializedStyles;
  };

  label: {
    base: SerializedStyles;
    notification: SerializedStyles;
  };
  utilityButton: {
    base: SerializedStyles;
    overflow: SerializedStyles;
  };
};

const useTabListStyles = (): StyleKeys => {
  const theme = useTheme();
  return {
    root: {
      base: css({
        display: 'flex',
        position: 'relative',
      }),
      attached: {
        hcOrOutlined: css({
          '&::after': {
            content: "' '",
            display: 'block',
            height: 16,
            width: '100%',
            maxWidth: 16,
            border: '1px solid transparent',
            borderTopColor: theme.color.stroke.decorative.default,
            borderTopLeftRadius: theme.borderRadius5,
            borderLeftColor: theme.color.stroke.decorative.default,
            position: 'absolute',
            top: 47,
            left: 0,
          },
          '&::before': {
            content: "' '",
            display: 'block',
            height: 16,
            width: '100%',
            maxWidth: 16,
            border: '1px solid transparent',
            borderTopColor: theme.color.stroke.decorative.default,
            borderTopRightRadius: theme.borderRadius5,
            borderRightColor: theme.color.stroke.decorative.default,
            position: 'absolute',
            top: 47,
            right: 0,
          },
        }),
        hc: css({
          '&::after': {
            borderTopColor: theme.color.foreground.default,
            borderLeftColor: theme.color.foreground.default,
          },
          '&::before': {
            borderTopColor: theme.color.foreground.default,
            borderRightColor: theme.color.foreground.default,
          },
        }),
        overflow: {
          base: css({
            '&::after': { maxWidth: 40 },
            '&::before': { maxWidth: 40 },
          }),
          hc: css({
            '&::after ': {
              maxWidth: 39,
              borderTopColor: theme.color.foreground.default,
              borderLeftColor: theme.color.foreground.default,
            },
            '&::before': {
              maxWidth: 39,
              borderTopColor: theme.color.foreground.default,
              borderRightColor: theme.color.foreground.default,
            },
          }),
          pill: css({
            '&::after ': {
              maxWidth: 45,
            },
          }),
        },

        roundNoOverflow: css({
          '&::after': { width: '0%' },
          '&::before': { width: '0%' },
        }),

        pill: css({
          '&::after': {
            borderTopLeftRadius: theme.borderRadius10,
            maxWidth: 27,
            height: theme.borderRadius10,
          },
          '&::before': {
            borderTopRightRadius: theme.borderRadius10,
            maxWidth: 25,
            height: theme.borderRadius10,
          },
        }),
      },
      online: {
        base: css({
          width: 'fit-content',
          maxWidth: '100%',
        }),

        contained: {
          base: css({
            backgroundColor: theme.color.background.container.default,
            boxShadow: `${theme.elevation.level4.boxShadow} ${theme.color.effects.dropShadow}`,
            borderRadius: theme.borderRadius4,
            paddingInline: theme.layout.size1,
          }),
          hc: css({
            border: `1px solid ${theme.color.foreground.default}`,
          }),
          pill: css({
            borderRadius: getPillBorder,
          }),
        },

        extendedHcOverflow: css({
          '&::after': {
            content: "' '",
            display: 'block',
            height: 2,
            width: '100%',
            maxWidth: 40,
            backgroundColor: theme.color.stroke.decorative.default,
            position: 'absolute',
            top: 46,
            left: 0,
          },
          '&::before': {
            content: "' '",
            display: 'block',
            height: 2,
            width: '100%',
            maxWidth: 40,
            backgroundColor: theme.color.stroke.decorative.default,
            position: 'absolute',
            top: 46,
            right: 0,
          },
        }),
      },
    },

    tabs: {
      base: css({
        height: 48,
        scrollBehavior: 'smooth',
        display: 'inline-flex',
        flex: '1 1 auto',
        overflow: 'hidden',
        boxSizing: 'border-box',
        flexFlow: 'row nowrap',
      }),

      onlineContained: css({
        gap: theme.layout.size1,
      }),

      roundNoOverflow: css({
        paddingLeft: 16,
        paddingRight: 16,
      }),

      pillNoOverflow: css({
        paddingLeft: theme.borderRadius10,
        paddingRight: theme.borderRadius9,
      }),

      noContentPanel: {
        base: css({
          paddingInline: 0,
        }),
        dropShadow: css({
          paddingInline: 4,
          marginInline: -4,
        }),
      },

      hc: css({
        '&::after': {
          content: "' '",
          display: 'flex',
          flex: '1 1 auto',
          borderBottom: `1px solid ${theme.color.foreground.default}`,
        },
      }),

      outlined: css({
        '&::after': {
          content: "' '",
          display: 'flex',
          flex: '1 1 auto',
          borderBottom: `1px solid ${theme.color.stroke.decorative.default}`,
        },
      }),

      extended: css({
        '&::after': {
          content: "' '",
          display: 'flex',
          flex: '1 1 auto',
          borderBottom: `2px solid ${theme.color.stroke.decorative.default}`,
        },
      }),

      dropShadow: {
        base: css({
          marginTop: '-4px',
          paddingTop: '4px',
          height: 52,
        }),

        overflow: css({
          marginLeft: -4,
          paddingLeft: 4,
          marginRight: -4,
          paddingRight: 4,
        }),
      },
    },

    tab: {
      base: css({
        padding: '0 16px',
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: theme.color.foreground.default,
        position: 'relative',

        '&::after': {
          content: "' '",
          display: 'block',
          height: 2,
          borderRadius: theme.borderRadius2,
          backgroundColor: 'transparent',
          position: 'absolute',
          bottom: 0,
          width: 'calc(100%)',
          zIndex: 1,
        },

        '&:hover': {
          backgroundColor: theme.color.neutral.background.hover,
          '&::after': {
            backgroundColor: theme.color.neutral.base.hover,
          },
        },

        '&:active': {
          backgroundColor: theme.color.neutral.background.active,
          '&::after': {
            backgroundColor: theme.color.neutral.base.active,
          },
        },

        ...doubleFocusRing({
          theme,
          outlineOffset: 2,
          shadowSize: 2,
          extension: '.vn-tab-list__label',
          hideOriginalOutline: true,
        }),
      }),
      disabled: css({
        color: theme.color.foreground.disabled,
        backgroundColor: 'transparent',
        cursor: 'default',

        // if tab is selected but disabled
        '&::after': {
          backgroundColor: 'transparent',
        },

        '&:hover': {
          backgroundColor: 'transparent',
          color: theme.color.foreground.disabled,
          '&::after': {
            backgroundColor: 'transparent',
          },
        },

        '&:active': {
          backgroundColor: 'transparent',
          color: theme.color.foreground.disabled,

          '&::after': {
            backgroundColor: 'transparent',
          },
        },
      }),
      hc: css({
        color: theme.color.primary.base.default,
        '&::after': {
          content: 'unset',
        },
        '&::before': {
          backgroundColor: 'red',
        },
        '&:hover': {
          backgroundColor: theme.color.neutral.base.hover,
          color: theme.color.primary.foreground.default,
        },
        '&:active': {
          backgroundColor: theme.color.neutral.base.active,
          color: theme.color.primary.foreground.default,
        },
      }),
      attached: {
        base: css({
          borderTopLeftRadius: theme.borderRadius,
          borderTopRightRadius: theme.borderRadius,
          border: `1px solid transparent`,
          borderBottom: 'none',
          '&::after': {
            width: 'calc(100% - 32px)',
          },
        }),
        hc: css({
          '&::before': {
            content: "' '",
            display: 'block',
            height: 1,
            position: 'absolute',
            backgroundColor: theme.color.foreground.default,
            bottom: 0,
            width: 'calc(100% + 2px)',
          },
        }),
        pill: css({
          borderTopLeftRadius: theme.borderRadius5,
          borderTopRightRadius: theme.borderRadius5,
        }),
      },

      outlined: css({
        '&::before': {
          content: "' '",
          display: 'block',
          height: 1,
          position: 'absolute',
          backgroundColor: theme.color.stroke.decorative.default,
          bottom: 0,
          width: 'calc(100% + 2px)',
        },
      }),

      online: css({
        '&::before': {
          content: "' '",
          display: 'block',
          height: 2,
          position: 'absolute',
          backgroundColor: theme.color.stroke.decorative.default,
          bottom: 0,
          width: '100%',
        },
      }),

      contained: css({
        '&::before': {
          content: 'unset',
        },
        '&::after': makeHorizontalVisualCue(theme, theme.layout.size4, 2),

        ':hover': {
          '&::after': { opacity: 1 },
        },
        ':active': {
          '&::after': { opacity: 1 },
        },
      }),

      onlineContained: css({
        borderRadius: theme.borderRadius3,
        marginBottom: theme.layout.size1,
        marginTop: theme.layout.size1,
      }),

      pill: css({
        borderRadius: getPillBorder,
      }),

      selected: {
        base: css({
          color: theme.color.primary.base.default,
          '&::after': {
            backgroundColor: theme.color.primary.base.default,
          },
          '&:hover': {
            color: theme.color.primary.base.hover,
            '&::after': {
              backgroundColor: theme.color.primary.base.hover,
            },
          },
          '&:active': {
            color: theme.color.primary.base.active,
            '&::after': {
              backgroundColor: theme.color.primary.base.active,
            },
          },
        }),
        hc: css({
          color: theme.color.primary.foreground.default,
          borderColor: 'transparent',
          backgroundColor: theme.color.primary.base.default,
          '&::before': {
            content: 'unset',
          },

          '&:hover': {
            backgroundColor: theme.color.neutral.base.hover,
            color: theme.color.primary.foreground.default,
          },
          '&:active': {
            backgroundColor: theme.color.neutral.base.active,
            color: theme.color.primary.foreground.default,
          },
        }),
        online: {
          base: css({
            '&:hover': {
              backgroundColor: theme.color.primary.background.hover,
            },
            '&:active': {
              backgroundColor: theme.color.primary.background.active,
            },
          }),
          hc: css({
            '&::before': {
              content: '" "',
              backgroundColor: theme.color.primary.base.default,
            },
            backgroundColor: 'transparent',
            color: theme.color.primary.base.default,
            '&:hover': {
              backgroundColor: theme.color.primary.background.hover,
              '&::before': {
                backgroundColor: theme.color.primary.background.hover,
              },
            },
            '&:active': {
              backgroundColor: theme.color.primary.background.active,
              '&::before': {
                backgroundColor: theme.color.primary.background.active,
              },
            },
          }),
          contained: {
            base: css({
              backgroundColor: theme.color.primary.background.active,
              color: theme.color.primary.base.active,
              '&::after': {
                opacity: 1,
                backgroundColor: theme.color.primary.base.active,
              },
              '&:hover': {
                '&::after': {
                  backgroundColor: theme.color.primary.base.hover,
                },
              },
            }),
            hc: css({
              backgroundColor: theme.color.primary.base.default,
              color: theme.color.primary.foreground.default,
            }),
          },
        },
        attached: {
          base: css({
            backgroundColor: theme.color.background.container.default,
            '&:hover': {
              backgroundColor: theme.color.background.container.default,
            },
            '&:active': {
              backgroundColor: theme.color.background.container.default,
            },
          }),

          hc: css({
            backgroundColor: theme.color.primary.base.default,
          }),
        },
        outlined: {
          base: css({
            borderColor: theme.color.stroke.decorative.default,
            borderBottomColor: 'transparent',
            backgroundColor: 'transparent',

            '&::before': {
              content: 'unset',
            },

            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&:active': {
              backgroundColor: 'transparent',
            },
          }),

          hc: css({
            borderColor: theme.color.stroke.decorative.default,
            borderBottomColor: 'transparent',
            backgroundColor: theme.color.primary.base.default,
          }),
        },
        dropShadow: css({
          boxShadow: `${theme.elevation.level4.boxShadow} ${theme.color.effects.dropShadow}`,
        }),
      },
      notification: css({
        gap: theme.layout.size1,
      }),
    },

    label: {
      base: css({
        minWidth: '72px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        ...theme.typography.labelRegular,
      }),
      notification: css({
        minWidth: 'auto',
      }),
    },

    utilityButton: {
      base: css({
        display: 'none',
        height: theme.layout.size8,
        width: theme.layout.size8,
        margin: '8px 4px',
      }),
      overflow: css({
        display: 'inherit',
      }),
    },
  };
};

export default useTabListStyles;
